import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { useHistory } from 'react-router-dom';
import pluralize from 'pluralize';
import { IconTimerFill, IconInformationOutline, IconAlertFill } from '@saucelabs/ds-icons';
import Button from '@saucelabs/ds-button';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';
import { format, parseISO } from 'date-fns';
import { Typography } from '@saucelabs/ds-typography';
import { Units } from '@saucelabs/sl-module-usage/lib/constants';

import {
  IS_ONE_CLICK_STORAGE_SET,
  TRIAL_EXPIRY_STORE_VALUE,
  ONE_DAY_MILLISECONDS,
  BILLING_INTERVALS,
  LAST_EMPTY_CREDITS_NOTICE,
} from '../../constants';
import {
  EVENTS,
  ONE_CLICK_BILLING_BUTTON,
  ONE_CLICK_BILLING_PLAN,
  trackEvent,
  trackStandardEvent,
} from '../../tracking';
import OneClickBillingPopover from './OneClickBillingPopover';
import TrialExpiryPopover from './TrialExpiryPopover';
import FreemiumEmptyCreditsPopover from './FreemiumEmptyCreditsPopover';

const MINUTES_PERCENTAGE_HIGH = 0.6;
const MINUTES_PERCENTAGE_MIDDLE = 0.25;

const PURCHASE_CONFIG_STORAGE_KEY = 'billing-unified/purchase_config';

const OPEN_STATES = {
  BILLING: 'BILLING',
  EXPIRY_REMINDER: 'EXPIRY_REMINDER',
  EMPTY_CREDITS: 'EMPTY_CREDITS',
};

const ICON_STYLES = 'h-3 w-3 p-0.25 mr-1 flex-shrink-0 fill-current text-gray-500';
const BEST_VALUE_STYLES = 'bg-blue-50 text-high-emphasis font-semibold py-0 px-0.5 my-0 mx-1 rounded text-sm';

const NEXT_REFILL_DATE_FORMAT = 'MMM d';

const getNextRefillDate = (date) => format(parseISO(date), NEXT_REFILL_DATE_FORMAT);

const OneClickBillingCta = ({
  freemiumRefillDate,
  isExpired,
  isFreemiumCapable,
  isFreemiumOnly,
  isMobile,
  isOldFreeTrial,
  isOnFreeTrialPlan,
  manualMinutes,
  manualMinutesRemaining,
  trialEnd,
  usageUnits,
  userId,
  vdcAutomatedMinutes,
  vdcAutomatedMinutesRemaining,
}) => {
  const [openState, setOpenState] = useState(undefined);
  const [daysToExpiry, setDaysToExpiry] = useState(0);
  const [daysToRefill, setDaysToRefill] = useState(0);
  const ctaRef = useRef(null);
  const history = useHistory();
  const flags = useFlags();

  const isManualLimitReached = isOldFreeTrial &&
    Number.isFinite(manualMinutesRemaining) &&
    manualMinutesRemaining <= 0;
  const isAutomatedLimitReached = isOldFreeTrial &&
    Number.isFinite(vdcAutomatedMinutesRemaining) &&
    vdcAutomatedMinutesRemaining <= 0;

  const manualMinutesNumber = isOldFreeTrial ? parseInt(manualMinutes, 10) : 0;
  const vdcAutomatedMinutesNumber = parseInt(vdcAutomatedMinutes, 10);

  const showBillingPopover = (isManualLimitReached || isAutomatedLimitReached || isExpired) &&
    !isFreemiumCapable && !flags?.webUiUsageBasedBilling;

  const isFreemiumCapableOnUsage = isFreemiumCapable && flags?.webUiUsageBasedBilling;

  const showAnnualPrices = flags?.growthOneClickBillingAnnualPrices &&
    !isFreemiumCapableOnUsage;

  const billingInterval = showAnnualPrices ?
    BILLING_INTERVALS.YEAR :
    BILLING_INTERVALS.MONTH;

  useEffect(() => {
    if (showBillingPopover) {
      setOpenState(OPEN_STATES.BILLING);
    }
  }, [showBillingPopover]);

  useEffect(() => {
    if (usageUnits?.total_available_usage_units <= 0) {
      // Empty credits freemium users
      const lastEmptyUsageUnitsNotice = localStorage.getItem(`${userId}:${LAST_EMPTY_CREDITS_NOTICE}`);
      let daysSinceLastNotice = Infinity;
      if (lastEmptyUsageUnitsNotice) {
        const lastEmptyUsageUnitsNoticeDate = (new Date(parseInt(lastEmptyUsageUnitsNotice, 10)));
        const today = new Date();
        daysSinceLastNotice = Math.ceil(
          (today - lastEmptyUsageUnitsNoticeDate) / ONE_DAY_MILLISECONDS,
        );
      }

      if (
        (isFreemiumCapable || isOnFreeTrialPlan) && daysSinceLastNotice > 1
      ) {
        if (usageUnits?.usage_unit === Units.Credits) {
          const refillDate = (new Date(freemiumRefillDate)).getDate();
          const today = (new Date()).getDate();
          setDaysToRefill(refillDate - today);
          setOpenState(OPEN_STATES.EMPTY_CREDITS);
        } else {
          setOpenState(OPEN_STATES.BILLING);
        }
      }
    }
  }, [usageUnits]);

  useEffect(() => {
    // Don't conflict with the regular billing popover
    // If the user is on freemium, trialEnd is null
    if (userId && trialEnd && !showBillingPopover) {
      const trialEndDate = new Date(0);
      trialEndDate.setSeconds(trialEnd);

      const now = new Date();
      const daysToEndOfTrial = Math.ceil((trialEndDate - now) / ONE_DAY_MILLISECONDS);

      if (daysToEndOfTrial <= 3) {
        let storeDays = daysToEndOfTrial;
        if (storeDays >= 2) storeDays = 3;
        if (storeDays <= 0) {
          // The zeroth day popover only contains credit based billing, so if
          // the user can't see it don't display it (the billing popover
          // should handle this state as well)
          if (!flags?.webUiUsageBasedBilling) return;
          storeDays = 0;
        }
        const storeValue = localStorage.getItem(`${userId}:${TRIAL_EXPIRY_STORE_VALUE}`);

        try {
          if (!storeValue || parseInt(storeValue, 10) > storeDays) {
            localStorage.setItem(`${userId}:${TRIAL_EXPIRY_STORE_VALUE}`, `${storeDays}`);
            setDaysToExpiry(daysToEndOfTrial);
            setOpenState(OPEN_STATES.EXPIRY_REMINDER);
            trackStandardEvent({
              subjectName: ONE_CLICK_BILLING_BUTTON.VIEW_EXPIRY,
              daysLeft: daysToEndOfTrial,
            });
          }
        } catch (error) {
          // Do nothing
        }
      }
    }
  }, [userId, trialEnd, showBillingPopover]);

  if (
    (Number.isNaN(manualMinutesNumber) || Number.isNaN(vdcAutomatedMinutesNumber)) &&
    !(isFreemiumCapable || isOnFreeTrialPlan)
  ) {
    return null;
  }

  if (isFreemiumCapable && !usageUnits) return null;

  // set the manual testing as the default type
  let testingType = ONE_CLICK_BILLING_PLAN.MANUAL;
  let minutesRemaining;
  let minutesTotal;
  let manualMinutesUsed;
  let vdcAutomatedMinutesUsed;

  if (!isFreemiumOnly) { // TODO: why not isFreemiumCapable?
    minutesRemaining = manualMinutesRemaining;
    minutesTotal = manualMinutesNumber;
    manualMinutesUsed = manualMinutes - manualMinutesRemaining;
    vdcAutomatedMinutesUsed = vdcAutomatedMinutesNumber - vdcAutomatedMinutesRemaining;

    // show pricing plans based on which is used more
    if ((manualMinutesUsed < vdcAutomatedMinutesUsed) ||
    // using the automated testing for users who reached both limits
    (isManualLimitReached && isAutomatedLimitReached)) {
      minutesRemaining = vdcAutomatedMinutesRemaining;
      testingType = ONE_CLICK_BILLING_PLAN.AUTOMATED;
      minutesTotal = vdcAutomatedMinutesNumber;
    }
  }

  const isManual = testingType === ONE_CLICK_BILLING_PLAN.MANUAL;

  const onPopoverToggle = () => {
    const hasOpenedPopover = window.sessionStorage.getItem(IS_ONE_CLICK_STORAGE_SET) === 'true';
    if (!hasOpenedPopover) {
      window.sessionStorage.setItem(IS_ONE_CLICK_STORAGE_SET, true);
    }

    if (openState === OPEN_STATES.BILLING) {
      trackStandardEvent({
        subjectName: ONE_CLICK_BILLING_BUTTON.ADD_PLAN,
        plan: testingType,
        interval: billingInterval,
      });
      setOpenState(undefined);
    }

    setOpenState(OPEN_STATES.BILLING);
  };

  const onCtaClick = () => {
    // When user is in UBB experiment, we dont' want to show the popover,
    // but instead we redirect them to Billing page for UBB.
    if (flags?.webUiUsageBasedBilling) {
      trackEvent(EVENTS.MAIN_NAV_ADD_PLAN_CTA, { billingMode: 'usage' });
      return history.push('/billing');
    }

    return onPopoverToggle();
  };

  const onCheckout = () => {
    let interval = billingInterval;

    if (openState === OPEN_STATES.EXPIRY_REMINDER) {
      interval = BILLING_INTERVALS.YEAR;

      trackStandardEvent({
        subjectName: ONE_CLICK_BILLING_BUTTON.UPGRADE_NOW,
        daysLeft: daysToExpiry,
      });
    } else if (isFreemiumCapable) {
      trackStandardEvent({
        subjectName: ONE_CLICK_BILLING_BUTTON.UPGRADE_NOW,
        plan: 'freemium',
      });
    } else {
      trackStandardEvent({
        subjectName: ONE_CLICK_BILLING_BUTTON.UPGRADE_NOW,
        plan: testingType,
        interval: billingInterval,
      });
    }

    if (
      (openState === OPEN_STATES.EXPIRY_REMINDER && flags?.webUiUsageBasedBilling) ||
      isFreemiumCapableOnUsage
    ) {
      history.push('/billing/purchase');
      return;
    }

    const purchaseSelector = isManual ? {
      purchase: {
        live: { concurrency: 1, interval },
      },
    } : {
      purchase: {
        vdc: { concurrency: 1, interval },
      },
    };

    // Reference: https://gitlab.tools.saucelabs.net/web-core/web-ui/commit/1bca7dc336c8edff464d12a3ae08da401ad1b6e2
    try {
      localStorage.setItem(
        PURCHASE_CONFIG_STORAGE_KEY,
        JSON.stringify(purchaseSelector.purchase),
      );
    } catch (error) {
      console.error(error);
    }
    history.push('/billing/checkout', purchaseSelector);
  };

  const onEditOrder = () => {
    trackStandardEvent({
      subjectName: ONE_CLICK_BILLING_BUTTON.EDIT_ORDER,
      plan: testingType,
      interval: billingInterval,
    });
    history.push('/billing/purchase', { purchase: { live: { concurrency: 1, interval: 'month' } } });
  };

  // https://saucedev.atlassian.net/browse/GRW-471
  // Update logic here once we want to handle users who have a credit card on file

  let checkoutCopy = isManual ? (
    <span>
      {showAnnualPrices ?
        'Upgrade Now • $39 /month' :
        'Upgrade Now • $49 /month'}
      <span className={BEST_VALUE_STYLES}>Best Value</span>
    </span>
  ) : (
    <span>
      {showAnnualPrices ?
        'Upgrade Now • $149 /month' :
        'Upgrade Now • $199 /month'}
      <span className={BEST_VALUE_STYLES}>Best Value</span>
    </span>
  );

  if (isFreemiumCapableOnUsage) {
    checkoutCopy = (
      <span>
        Buy the starter plan ($50.00)
      </span>
    );
  }

  let ctaIconColor = 'red';

  if ((isFreemiumCapable || isOnFreeTrialPlan) && usageUnits?.total_available_usage_units > 0) ctaIconColor = 'green';

  if (!isExpired && !isFreemiumOnly) {
    if ((minutesRemaining / minutesTotal) > MINUTES_PERCENTAGE_HIGH) ctaIconColor = 'green';
    else if ((minutesRemaining / minutesTotal) > MINUTES_PERCENTAGE_MIDDLE) ctaIconColor = 'yellow';
  }

  const CtaIcon = () => {
    switch (ctaIconColor) {
      case 'green':
        return <IconTimerFill className={ICON_STYLES} />;
      case 'yellow':
      case 'red':
        return <IconAlertFill className={ICON_STYLES} />;
      default: {
        if (openState === OPEN_STATES.BILLING) {
          return <IconInformationOutline className={ICON_STYLES} />;
        }
        return <IconTimerFill className={ICON_STYLES} />;
      }
    }
  };

  let ctaContent = isExpired ? 'Your free trial has expired.' : (
    <span>
      <Typography
        className="font-bold"
        component="span"
      >
        {minutesRemaining}
        {' '}
      </Typography>
      <Typography component="span">
        {`${testingType.toLowerCase()} minutes left`}
      </Typography>
    </span>
  );

  if (isOnFreeTrialPlan && usageUnits?.usage_unit === Units.Seconds) {
    minutesRemaining = Math.max(Math.floor(usageUnits?.total_available_usage_units / 60 || 0), 0);
    testingType = ONE_CLICK_BILLING_PLAN.AUTOMATED;
    ctaContent = (
      <>
        <Typography
          className="font-semibold"
          component="span"
          variant="body"
        >
          {minutesRemaining.toLocaleString()}
          {' '}
          {`manual ${pluralize('minute', minutesRemaining)} left`}
        </Typography>
      </>
    );
  }

  let ctaTitle = `${minutesRemaining} minutes remaining for ${testingType} testing`;

  if (isFreemiumCapable && usageUnits?.usage_unit === Units.Credits) {
    const minCreditsLeft = Math.max(usageUnits?.total_available_usage_units || 0, 0);

    ctaContent = (
      <>
        <Typography
          className="font-semibold"
          component="span"
          variant="body"
        >
          {minCreditsLeft.toLocaleString()}
          {' '}
          {`${pluralize('credit', minCreditsLeft)} left`}
        </Typography>
        {freemiumRefillDate && (
          <Typography
            className="font-light pl-1"
            component="span"
            variant="body"
          >
            {`(next refill - ${getNextRefillDate(freemiumRefillDate)})`}
          </Typography>
        )}
      </>
    );
    ctaTitle = `${minCreditsLeft} credits remaining for testing`;
  }

  return (
    <div
      className={classNames('my-auto mx-0 flex relative', {
        mobile: isMobile,
      })}
    >
      <div
        ref={ctaRef}
        className={classNames('bg-gray-50 h-full rounded flex items-center font-normal px-1.5 py-1', {
          'bg-green-50 text-high-emphasis text-opacity-80': ctaIconColor === 'green',
          'bg-orange-50 text-orange-600': ctaIconColor === 'yellow',
          'bg-red-50 text-red-900': ctaIconColor === 'red',
        })}
      >
        <CtaIcon />
        <div
          className="truncate text-opacity-80 text-gray-900 mr-2"
          title={ctaTitle}
        >
          {ctaContent}
        </div>
        <Button
          buttonProps={{ size: 'sm', color: 'primary', 'data-qa': 'one-click-billing-button' }}
          label="Add"
          onPress={onCtaClick}
        >
          {'Upgrade'}
        </Button>
      </div>
      {openState === OPEN_STATES.BILLING && (
        <OneClickBillingPopover
          checkoutCopy={checkoutCopy}
          isAutomated={testingType === ONE_CLICK_BILLING_PLAN.AUTOMATED}
          isFreemiumCapableOnUsage={isFreemiumCapableOnUsage}
          isMobile={isMobile}
          onCheckout={onCheckout}
          onClose={() => setOpenState(undefined)}
          onEditOrder={onEditOrder}
        />
      )}
      {openState === OPEN_STATES.EXPIRY_REMINDER && (
        <TrialExpiryPopover
          daysToExpiry={daysToExpiry}
          isManual={isManual}
          onCheckout={onCheckout}
          onClose={() => setOpenState(undefined)}
        />
      )}
      {openState === OPEN_STATES.EMPTY_CREDITS && (
        <FreemiumEmptyCreditsPopover
          daysToRefill={daysToRefill}
          onClose={() => {
            localStorage.setItem(`${userId}:${LAST_EMPTY_CREDITS_NOTICE}`, Date.now());
            setOpenState(undefined);
          }}
        />
      )}
    </div>
  );
};

OneClickBillingCta.propTypes = {
  freemiumRefillDate: PropTypes.string,
  isExpired: PropTypes.bool,
  isFreemiumCapable: PropTypes.bool,
  isFreemiumOnly: PropTypes.bool,
  isMobile: PropTypes.bool,
  isOldFreeTrial: PropTypes.bool,
  isOnFreeTrialPlan: PropTypes.bool,
  manualMinutes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]), // user.monthly_minutes.manual
  manualMinutesRemaining: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // user.manual_minutes
  trialEnd: PropTypes.number,
  usageUnits: PropTypes.shape({
    available_usage_units: PropTypes.arrayOf(
      PropTypes.shape({
        expiration_time: PropTypes.string.isRequired,
        grant_time: PropTypes.string.isRequired,
        source: PropTypes.string.isRequired,
        usage_units: PropTypes.number.isRequired,
      }),
    ),
    total_available_usage_units: PropTypes.number.isRequired,
    usage_unit: PropTypes.string.isRequired,
  }),
  userId: PropTypes.string,
  vdcAutomatedMinutes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]), // user.monthly_minutes.automated
  vdcAutomatedMinutesRemaining: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]), // user.minutes
};

OneClickBillingCta.defaultProps = {
  freemiumRefillDate: null,
  isExpired: false,
  isFreemiumCapable: false,
  isFreemiumOnly: false,
  isMobile: false,
  isOldFreeTrial: false,
  isOnFreeTrialPlan: false,
  manualMinutes: null,
  manualMinutesRemaining: null,
  trialEnd: null,
  usageUnits: null,
  userId: null,
  vdcAutomatedMinutes: null,
  vdcAutomatedMinutesRemaining: null,
};

export default OneClickBillingCta;
